import React from "react";
import { axios, SideNav, useEffect, useState, useMemo, useNavigate, decryptData, Header, handleApiError, Skeleton, FormatDate, BreadCrumbs, Modal, CustomModal, SuccessfullVector } from "./../MentorImport";
import * as XLSX from "xlsx";
import DownloadExcel from "../../_common/DownloadExcel";
import "./CourseEnrolledUsers.css";
import "../../AdminPortal/UploadCourses/TotalUploadedCourses/TotalUploadedCourses.css";
import { ReactComponent as Revoke } from "../../../Assets/Revoke.svg";
import { ReactComponent as RevokeArrow } from "../../../Assets/RevokeArrow.svg";
import { ReactComponent as DeleteWarning } from "../../../Assets/VectordeleteTsk.svg";
import Select, { components } from "react-select";

const statusOptions = [
  { label: "Enrolled", value: "enrolled" },
  { label: "Ongoing", value: "ongoing" },
  { label: "Completed", value: "completed" },
];

const CheckboxOption = ({ getStyles, Icon, isDisabled, isFocused, isSelected, children, innerProps, ...rest }) => {
  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isSelected}
      getStyles={getStyles}
      innerProps={innerProps}
    >
      <input
        type="checkbox"
        checked={isSelected}
        readOnly
        className="form-check-input me-2 mt-0 pointer"
      />
      {children}
    </components.Option>
  );
};

const StatusValueContainer = ({ children, ...props }) => {
  const selected = props.getValue() || [];
  if (selected.length === 0) {
    return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
  }

  let labelText = "All Statuses";
  if (selected.length === 1) {
    labelText = selected[0].label;
  } else if (selected.length > 1 && selected.length < statusOptions.length) {
    labelText = `${selected.length} Selected`;
  }

  return (
    <components.ValueContainer {...props}>
      <span
        style={{
          fontSize: "0.8rem",
          color: "#343435",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          userSelect: "none",
        }}
      >
        {labelText}
      </span>
    </components.ValueContainer>
  );
};

const selectStyles = {
  container: (provided) => ({
    ...provided,
    fontSize: "0.8rem",
    width: "9.5rem",
    minWidth: "9.5rem",
  }),
  control: (provided) => ({
    ...provided,
    minHeight: "1.938rem",
    height: "1.938rem",
    borderColor: "#d5dce2",
    borderRadius: "0.25rem",
    boxShadow: "none",
    cursor: "pointer",
    "&:hover": {
      borderColor: "#002c3f",
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "1.938rem",
    padding: "0 6px",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "hidden",
  }),
  placeholder: (provided) => ({
    ...provided,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    margin: "0",
    color: "#6c757d",
    fontSize: "0.8rem",
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
    padding: "0px",
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "1.938rem",
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
  clearIndicator: (provided) => ({
    ...provided,
    padding: "2px",
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    fontSize: "0.8rem",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f0f4f8" : "white",
    color: "#343435",
    display: "flex",
    alignItems: "center",
    padding: "6px 10px",
    cursor: "pointer",
  }),
};

const isUserOverdue = (user) => {
  const isCompleted = String(user?.courseStatusName || "").toLowerCase() === "completed";
  const isRevoked = String(user?.courseStatusName || "").toLowerCase() === "revoked";
  if (isCompleted || isRevoked) return false;
  if (!user?.courseEndDate) return false;
  const endDate = new Date(user.courseEndDate);
  return !isNaN(endDate.getTime()) && new Date() > endDate;
};

const CourseEnrolledUsers = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedStatusOptions, setSelectedStatusOptions] = useState([]);
  const [isOverdueOnly, setIsOverdueOnly] = useState(false);
  const [quizResultFilter, setQuizResultFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [nameSortOrder, setNameSortOrder] = useState("asc");
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    resultsPerPage: 10,
  });
  const [revokeModalState, setRevokeModalState] = useState({
    isModalOpen: false,
    modalType: "",
    modalTitle: "",
    modalMessage: "",
    icon: null,
    userId: 0,
    modalState: "",
  });
  const navigate = useNavigate();

  const courseId = sessionStorage.getItem("instructorCourseId");
  const courseTitle = sessionStorage.getItem("instructorCourseTitle");

  let parsedObject;
  const data = localStorage.getItem("userData");
  data ? (parsedObject = decryptData(data, process.env.REACT_APP_USER_KEY)) : navigate("/error/statusCode=400");
  const userToken = parsedObject?.token;

  useEffect(() => {
    if (!courseId) {
      navigate("/lms/assigned-courses");
      return;
    }

    const fetchEnrolledUsers = async () => {
      const url = `${process.env.REACT_APP_API_URL_DOTNET}/Course/getEnrolledUsersForIntructor?courseId=${courseId}`;
      const headers = {
        Authorization: `Bearer ${userToken}`,
      };

      try {
        const response = await axios.get(url, { headers });
        if (response.status === 200) {
          setUsers(response.data.result);
          setIsLoading(false);
        } else {
          throw new Error(`Unexpected response status: ${response.status}`);
        }
      } catch (error) {
        const statusCode = error.response ? error.response : null;
        handleApiError(statusCode, navigate);
      }
    };

    fetchEnrolledUsers();
  }, [courseId, navigate, userToken]);

  const handleViewModules = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedUser(null);
  };


  const handleRevokeCancel = () => {
    setRevokeModalState((prevState) => ({
      ...prevState,
      isModalOpen: false,
      modalType: "",
      modalTitle: "",
      modalMessage: "",
      icon: null,
      userId: 0,
    }));

    if (revokeModalState.modalState === "success") {
      setRevokeModalState((prevState) => ({
        ...prevState,
        modalState: "",
      }));
    }
  };

  const handleRevokeClick = (userId) => {
    setRevokeModalState((prevState) => ({
      ...prevState,
      isModalOpen: true,
      modalType: "confirm",
      modalTitle: "Revoke",
      modalMessage: "Are you sure you want to revoke this user? The user will be revoked from the enrolled list",
      icon: <RevokeArrow />,
      userId,
    }));
  };

  const handleRevokeConfirm = async () => {
    const url = `${process.env.REACT_APP_API_URL_DOTNET}/Course/manageEnrollment`;
    const headers = {
      Authorization: `Bearer ${userToken}`,
    };
    const body = {
      courseId,
      userId: [revokeModalState.userId],
      assignedById: parsedObject?.decoded?.Id,
      isAssigned: false,
      courseName: courseTitle,
      description: sessionStorage.getItem("instructorCourseDescription") || "",
    };

    try {
      const response = await axios.post(url, body, { headers });
      if (response.status === 200) {
        const revokedId = response?.data?.result?.[0]?.id || revokeModalState.userId;
        setUsers((prevUsers) => prevUsers.filter((user) => (user.userId || user.id) !== revokedId));
        setRevokeModalState((prevState) => ({
          ...prevState,
          isModalOpen: true,
          modalType: "success",
          modalTitle: "User Revoked",
          modalMessage: "User revoked successfully",
          icon: <SuccessfullVector />,
          modalState: "success",
          userId: 0,
        }));
      } else {
        setRevokeModalState((prevState) => ({
          ...prevState,
          isModalOpen: true,
          modalType: "error",
          modalTitle: "User Not Revoked",
          modalMessage: "Error occured while revoking user",
          icon: <DeleteWarning />,
          userId: 0,
        }));
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (error) {
      const statusCode = error.response ? error.response : null;
      handleApiError(statusCode, navigate);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "Completed":
        return "status-completed";
      case "Ongoing":
        return "status-ongoing";
      case "Enrolled":
        return "status-enrolled";
      default:
        return "status-default";
    }
  };

  const getProgressPercentage = (completed, total) => {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  };

  const getModuleQuizName = (module) => {
     const rawName = module?.quizName || module?.moduleQuizName || module?.testName || module?.assessmentName || `${module?.moduleName || "Module"} Quiz`;
     // Remove .csv extension if present
     return rawName.replace(/\.csv$/i, "");
  };

  const getPassMark = (attempt, module) => {
    const passMark = attempt?.cutOffPercentage ?? attempt?.cutoffPercentage ?? module?.cutOffPercentage ?? module?.cutoffPercentage;
    return passMark !== undefined && passMark !== null && passMark !== "" ? Number(passMark) : null;
  };

  const getAttemptQuestions = (attempt, module) => {
    const correctQuestions =
      attempt?.correctQuestions ??
      attempt?.correctQuestionCount ??
      attempt?.quizScore ??
      attempt?.score ??
      attempt?.obtainedMarks ??
      attempt?.marksObtained ??
      module?.correctQuestions ??
      module?.quizScore ??
      null;

    const totalQuestions =
      attempt?.totalQuestions ??
      attempt?.totalQuizQuestions ??
      attempt?.questionCount ??
      attempt?.totalMarks ??
      module?.totalQuestions ??
      module?.totalQuizQuestions ??
      null;

    return {
      correctQuestions,
      totalQuestions,
    };
  };

  const normalizeQuizAttempts = (module) => {
    const quizName = getModuleQuizName(module);

    const attemptGroups = [
      module?.quizAttemptScores,
      module?.quizAttempts,
      module?.attempts,
      module?.attemptList,
      module?.quizResultList,
      module?.quizResults,
      module?.resultList,
      module?.result,
    ];

    const attempts = attemptGroups.find((group) => Array.isArray(group) && group.length > 0);

    if (!attempts && module?.isCompleted === false) {
      return [
        {
          quizName,
          attemptNo: 0,
          correctQuestions: null,
          totalQuestions: module?.totalQuestions ?? module?.totalQuizQuestions ?? null,
          cutoffPercentage: module?.cutOffPercentage ?? module?.cutoffPercentage ?? null,
        },
      ];
    }

    if (Array.isArray(attempts) && attempts.length > 0) {
      const normalized = attempts.map((attempt, index) => ({
        quizName: attempt?.quizName || attempt?.testName || attempt?.assessmentName || quizName,
        attemptNo: attempt?.attempt ?? attempt?.attemptNo ?? attempt?.attemptNumber ?? index + 1,
        ...getAttemptQuestions(attempt, module),
        cutoffPercentage: getPassMark(attempt, module),
      }));

      return normalized.sort((a, b) => Number(b.attemptNo) - Number(a.attemptNo));
    }

    if (module?.totalQuizQuestions > 0 || module?.totalQuestions > 0 || (module?.quizScore !== undefined && module?.quizScore !== null)) {
      const { correctQuestions, totalQuestions } = getAttemptQuestions(module, module);
      const cutoffPercentage = getPassMark(module, module);

      if ((correctQuestions === null || correctQuestions === undefined) && (totalQuestions === null || totalQuestions === undefined)) {
        return [
          {
            quizName,
            attemptNo: 0,
            correctQuestions: null,
            totalQuestions: null,
            cutoffPercentage: null,
          },
        ];
      }

      return [
        {
          quizName,
          attemptNo: 1,
          correctQuestions,
          totalQuestions,
          cutoffPercentage,
        },
      ];
    }

    return [
      {
        quizName,
        attemptNo: 0,
        correctQuestions: null,
        totalQuestions: null,
        cutoffPercentage: null,
      },
    ];
  };

  const getAttemptResultLabel = (attempt) => {
    if (
      attempt?.attemptNo === 0 ||
      attempt?.cutoffPercentage === null ||
      attempt?.cutoffPercentage === undefined ||
      attempt?.correctQuestions === null ||
      attempt?.correctQuestions === undefined ||
      attempt?.totalQuestions === null ||
      attempt?.totalQuestions === undefined
    ) {
      return "N/A";
    }

    const percentage = Number(attempt.totalQuestions) === 0
      ? 0
      : (Number(attempt.correctQuestions) / Number(attempt.totalQuestions)) * 100;

    return percentage >= Number(attempt.cutoffPercentage) ? "Pass" : "Fail";
  };

  const getAttemptStatusClass = (attempt) => {
    const result = getAttemptResultLabel(attempt);
    if (result === "N/A") return "module-neutral";
    return result === "Pass" ? "module-completed" : "module-pending";
  };

  const getUserDisplayName = (user) => {
    return `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
  };

  const hasAtLeastOneQuizName = (user) => {
    if (!Array.isArray(user?.moduleList) || user.moduleList.length === 0) {
      return false;
    }

    return user.moduleList.some((module) => {
      const quizName = module?.quizName;
      return quizName !== null && quizName !== undefined && String(quizName).trim() !== "";
    });
  };

  const getUserQuizResult = (user) => {
    if (!Array.isArray(user?.moduleList) || user.moduleList.length === 0) {
      return "Not Attempted";
    }

    const latestAttemptResults = user.moduleList
      .map((module) => {
        const validAttempts = normalizeQuizAttempts(module)
          .filter((attempt) => Number(attempt?.attemptNo) > 0)
          .sort((a, b) => Number(b.attemptNo) - Number(a.attemptNo));

        if (validAttempts.length === 0) return null;

        const latestAttempt = validAttempts[0];
        const attemptResult = getAttemptResultLabel(latestAttempt);
        return attemptResult === "Pass" || attemptResult === "Fail" ? attemptResult : null;
      })
      .filter(Boolean);

    if (latestAttemptResults.length === 0) return "Not Attempted";
    return latestAttemptResults.includes("Fail") ? "Fail" : "Pass";
  };

  const processedUsers = useMemo(() => {
    if (!Array.isArray(users)) return [];

    let result = [...users];

    if (selectedStatusOptions && selectedStatusOptions.length > 0) {
      const selectedValues = selectedStatusOptions.map((opt) => opt.value);
      result = result.filter((user) =>
        selectedValues.includes(String(user?.courseStatusName || "").toLowerCase())
      );
    }

    if (isOverdueOnly) {
      result = result.filter((user) => isUserOverdue(user));
    }

    if (quizResultFilter !== "all") {
      result = result.filter((user) => getUserQuizResult(user).toLowerCase() === quizResultFilter);
    }

    if (searchQuery.trim()) {
      const normalizedQuery = searchQuery.trim().toLowerCase();
      result = result.filter(
        (user) =>
          getUserDisplayName(user).toLowerCase().includes(normalizedQuery) ||
          String(user?.email || user?.Email || "").toLowerCase().includes(normalizedQuery)
      );
    }

    result.sort((a, b) => {
      const nameA = getUserDisplayName(a).toLowerCase();
      const nameB = getUserDisplayName(b).toLowerCase();
      return nameSortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });

    return result;
  }, [users, selectedStatusOptions, isOverdueOnly, quizResultFilter, searchQuery, nameSortOrder]);

  const totalDataLength = processedUsers.length;
  const totalPaginationPages = Math.max(1, Math.ceil(totalDataLength / paginationState.resultsPerPage));
  const arrayStartIndex = (paginationState.currentPage - 1) * paginationState.resultsPerPage;
  const arrayEndIndex = arrayStartIndex + paginationState.resultsPerPage;
  const currentPageUsers = processedUsers.slice(arrayStartIndex, arrayEndIndex);

  const getPaginationItems = () => {
    if (totalPaginationPages <= 7) {
      return Array.from({ length: totalPaginationPages }, (_, i) => i + 1);
    }

    const currentPage = paginationState.currentPage;
    const items = [1];

    if (currentPage > 3) {
      items.push("left-ellipsis");
    }

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPaginationPages - 1, currentPage + 1);

    for (let page = startPage; page <= endPage; page++) {
      items.push(page);
    }

    if (currentPage < totalPaginationPages - 2) {
      items.push("right-ellipsis");
    }

    items.push(totalPaginationPages);
    return items;
  };

  const paginationItems = getPaginationItems();

  useEffect(() => {
    setPaginationState((prevState) => {
      const maxPage = Math.max(1, Math.ceil(totalDataLength / prevState.resultsPerPage));
      if (prevState.currentPage > maxPage) {
        return {
          ...prevState,
          currentPage: maxPage,
        };
      }
      return prevState;
    });
  }, [totalDataLength, paginationState.resultsPerPage]);

  const exportToExcel = () => {
    if (!processedUsers || processedUsers.length === 0) return;

    const safeCourseName = (courseTitle || "Course")
      .replace(/[\\/:*?"<>|]/g, "")
      .trim();

    const now = new Date();
    const pad = (value) => String(value).padStart(2, "0");
    const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

    const getQuizKey = (module, fallbackIndex) =>
      module?.moduleId || getModuleQuizName(module) || module?.moduleName || `quiz-${fallbackIndex}`;

    const quizRegistry = [];
    const quizIndexByKey = {};

    processedUsers.forEach((user) => {
      (user.moduleList || []).forEach((module, moduleIndex) => {
        const key = getQuizKey(module, moduleIndex + 1);
        if (quizIndexByKey[key] === undefined) {
          quizIndexByKey[key] = quizRegistry.length;
          quizRegistry.push({ key });
        }
      });
    });

    const maxAttemptsByQuiz = {};
    quizRegistry.forEach((quiz) => {
      maxAttemptsByQuiz[quiz.key] = 0;
    });

    processedUsers.forEach((user) => {
      (user.moduleList || []).forEach((module, moduleIndex) => {
        const key = getQuizKey(module, moduleIndex + 1);
        const attempts = normalizeQuizAttempts(module).filter((attempt) => Number(attempt?.attemptNo) > 0);
        if (attempts.length > maxAttemptsByQuiz[key]) {
          maxAttemptsByQuiz[key] = attempts.length;
        }
      });
    });

    quizRegistry.forEach((quiz) => {
      if (maxAttemptsByQuiz[quiz.key] === 0) {
        maxAttemptsByQuiz[quiz.key] = 1;
      }
    });

    const orderedColumns = ["Name", "Email", "Status", "Progress", "Start Date", "End Date", "Assignment"];
    quizRegistry.forEach((quiz, quizIndex) => {
      for (let attemptNo = 1; attemptNo <= maxAttemptsByQuiz[quiz.key]; attemptNo++) {
        orderedColumns.push(`Quiz ${quizIndex + 1} Attempt ${attemptNo} Score`);
        orderedColumns.push(`Quiz ${quizIndex + 1} Attempt ${attemptNo} Pass/Fail`);
      }
    });

    const rows = processedUsers.map((user) => {
      const row = {
        Name: `${user.firstName} ${user.lastName}`,
        Email: user.email || user.Email || "N/A",
        Status: user.courseStatusName,
        Progress: `${user.completedModuleCount}/${user.totalModuleCount}`,
        "Start Date": FormatDate(user.courseStartDate),
        "End Date": FormatDate(user.courseEndDate),
        Assignment: user.assignmentUrl || "N/A",
      };

      const userModulesByKey = {};
      (user.moduleList || []).forEach((module, moduleIndex) => {
        const key = getQuizKey(module, moduleIndex + 1);
        userModulesByKey[key] = module;
      });

      quizRegistry.forEach((quiz, quizIndex) => {
        const module = userModulesByKey[quiz.key];
        const normalizedAttempts = module
          ? normalizeQuizAttempts(module)
              .filter((attempt) => Number(attempt?.attemptNo) > 0)
              .sort((a, b) => Number(a.attemptNo) - Number(b.attemptNo))
          : [];

        for (let attemptNo = 1; attemptNo <= maxAttemptsByQuiz[quiz.key]; attemptNo++) {
          const scoreColumnName = `Quiz ${quizIndex + 1} Attempt ${attemptNo} Score`;
          const statusColumnName = `Quiz ${quizIndex + 1} Attempt ${attemptNo} Pass/Fail`;
          const attempt = normalizedAttempts.find((item) => Number(item.attemptNo) === attemptNo);

          if (!attempt) {
            row[scoreColumnName] = "N/A";
            row[statusColumnName] = "N/A";
            continue;
          }

          const score =
            attempt.correctQuestions === null ||
            attempt.correctQuestions === undefined ||
            attempt.totalQuestions === null ||
            attempt.totalQuestions === undefined
              ? "N/A"
              : `${attempt.correctQuestions}/${attempt.totalQuestions}`;

          const result = getAttemptResultLabel(attempt);
          row[scoreColumnName] = score;
          row[statusColumnName] = result;
        }
      });

      return row;
    });

    const wsData = [orderedColumns];
    rows.forEach((row) => {
      wsData.push(orderedColumns.map((col) => row[col] ?? ""));
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Enrolled Users");
    const excelBase64 = XLSX.write(wb, { bookType: "xlsx", type: "base64" });
    DownloadExcel(excelBase64, `${safeCourseName}_${timestamp}`);
  };

  const attemptRows = (selectedUser?.moduleList && selectedUser.moduleList.length > 0)
    ? selectedUser.moduleList.flatMap((module) => normalizeQuizAttempts(module).map((attempt) => ({ module, attempt })))
    : [];

  const moduleGroupIndex = {};
  let _groupCounter = 0;
  attemptRows.forEach(({ module }) => {
    const id = module?.moduleId ?? module?.moduleName ?? module?.quizName ?? JSON.stringify(module);
    if (moduleGroupIndex[id] === undefined) {
      moduleGroupIndex[id] = _groupCounter++;
    }
  });

  return (
    <>
      <div className="assigned-course-users-page">
        <div style={{ marginBottom: "3rem" }}>
          <Header />
          <SideNav />
        </div>
        <div className="container-fluid total-uploaded-courses-table-container d-flex flex-column">
          <div style={{ marginLeft: "1rem" }}>
            <div className="row">
              <div className="col-12">
                <div className="total-uploaded-courses-nav-bar">
                  <BreadCrumbs dashboardLink="/lms/assigned-courses" />
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="total-uploaded-courses-heading">
                  <p>{courseTitle}</p>
                </div>
              </div>
            </div>

            <div className="total-uploaded-courses-filter-wrapper course-enrolled-filter-wrapper-row">
              <div className="total-uploaded-courses-search">
                <div className="total-uploaded-courses-search-icon" />
                <input
                  type="text"
                  className="total-uploaded-courses-input-bar"
                  placeholder="Search by employee name..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPaginationState((prevState) => ({
                      ...prevState,
                      currentPage: 1,
                    }));
                  }}
                  disabled={isLoading}
                />
              </div>

              <div className="course-enrolled-actions-wrap">
                <div className="course-enrolled-filter-wrap d-flex align-items-center gap-2">
                  <label className="mb-0 course-enrolled-filter-label">Status:</label>
                  <Select
                    isMulti
                    isSearchable={false}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{
                      Option: CheckboxOption,
                      ValueContainer: StatusValueContainer,
                      MultiValue: () => null,
                    }}
                    options={statusOptions}
                    styles={selectStyles}
                    placeholder="All Statuses"
                    value={selectedStatusOptions}
                    onChange={(selected) => {
                      setSelectedStatusOptions(selected || []);
                      setPaginationState((prevState) => ({
                        ...prevState,
                        currentPage: 1,
                      }));
                    }}
                    isDisabled={isLoading}
                  />
                </div>

                <div className="course-enrolled-filter-wrap d-flex align-items-center gap-1">
                  <div className="form-check d-flex align-items-center gap-2 mb-0">
                    <input
                      className="form-check-input pointer mt-0"
                      type="checkbox"
                      id="overdueOnlyFilter"
                      checked={isOverdueOnly}
                      onChange={(e) => {
                        setIsOverdueOnly(e.target.checked);
                        setPaginationState((prevState) => ({
                          ...prevState,
                          currentPage: 1,
                        }));
                      }}
                      disabled={isLoading}
                    />
                    <label htmlFor="overdueOnlyFilter" className="form-check-label pointer course-enrolled-filter-label mb-0">
                      Overdue
                    </label>
                  </div>
                </div>

                <div className="course-enrolled-filter-wrap d-flex align-items-center gap-2">
                  <label htmlFor="quizResultFilter" className="mb-0 course-enrolled-filter-label">Quiz:</label>
                  <select
                    id="quizResultFilter"
                    className="form-select form-select-sm course-enrolled-filter-select course-enrolled-quiz-select"
                    value={quizResultFilter}
                    onChange={(e) => {
                      setQuizResultFilter(e.target.value);
                      setPaginationState((prevState) => ({
                        ...prevState,
                        currentPage: 1,
                      }));
                    }}
                    disabled={isLoading}
                  >
                    <option value="all">All</option>
                    <option value="pass">Pass</option>
                    <option value="fail">Fail</option>
                  </select>
                </div>

                <div className="course-enrolled-filter-wrap d-flex align-items-center gap-2">
                  <label htmlFor="nameSortOrder" className="mb-0 course-enrolled-filter-label">Sort:</label>
                  <select
                    id="nameSortOrder"
                    className="form-select form-select-sm course-enrolled-filter-select course-enrolled-sort-select"
                    value={nameSortOrder}
                    onChange={(e) => {
                      setNameSortOrder(e.target.value);
                      setPaginationState((prevState) => ({
                        ...prevState,
                        currentPage: 1,
                      }));
                    }}
                    disabled={isLoading}
                  >
                    <option value="asc">Name (A-Z)</option>
                    <option value="desc">Name (Z-A)</option>
                  </select>
                </div>

                <p className="mb-0 course-enrolled-total-count">Enrolled Users ({processedUsers.length})</p>
                <button
                  className="btn btn-sm course-module-toggle-btn"
                  onClick={exportToExcel}
                  disabled={isLoading || processedUsers.length === 0}
                >
                  Export
                </button>
              </div>
            </div>
          

            <table className="table table-striped-custom course-enrolled-table">
            <thead>
              <tr>
                <th className="col course-enrolled-table-head" style={{ width: "5%" }}>S.No.</th>
                <th className="col course-enrolled-table-head">Name</th>
                <th className="col course-enrolled-table-head">Email</th>
                <th className="col course-enrolled-table-head">Status</th>
                <th className="col course-enrolled-table-head text-center">Progress</th>
                <th className="col course-enrolled-table-head text-center">Start Date</th>
                <th className="col course-enrolled-table-head text-center">End Date</th>
                <th className="col course-enrolled-table-head text-center">Quiz Scores</th>
                <th className="col course-enrolled-table-head text-center">Assignment</th>
                <th className="col course-enrolled-table-head text-center">Revoke</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [1, 2, 3, 4].map((item) => (
                  <tr key={item}>
                    <td><Skeleton height={20} width={20} /></td>
                    <td><Skeleton height={20} width={120} /></td>
                    <td><Skeleton height={20} width={150} /></td>
                    <td><Skeleton height={20} width={80} /></td>
                    <td><Skeleton height={20} width={150} /></td>
                    <td><Skeleton height={20} width={90} /></td>
                    <td><Skeleton height={20} width={90} /></td>
                    <td><Skeleton height={20} width={60} /></td>
                    <td><Skeleton height={20} width={60} /></td>
                    <td><Skeleton height={20} width={24} /></td>
                  </tr>
                ))
              ) : currentPageUsers.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-5">
                    <p className="course-enrolled-empty">
                      No users match the selected filters.
                    </p>
                  </td>
                </tr>
              ) : (
                currentPageUsers.map((user, ind) => (
                  <tr className={`report-table-tr ${ind % 2 === 0 ? "enrolled-striped-even-rows" : "enrolled-striped-odd-rows"}`} key={user.userId || user.id || `${user.firstName}-${user.lastName}-${arrayStartIndex + ind}`}>
                    <td className="bg-transparent" style={{ verticalAlign: "middle" }}>{arrayStartIndex + ind + 1}</td>
                    <td className="bg-transparent" style={{ verticalAlign: "middle" }}>
                      <span className="course-enrolled-user-name">
                        {user.firstName} {user.lastName}
                      </span>
                    </td>
                    <td className="bg-transparent" style={{ verticalAlign: "middle" }}>
                      <span className="course-enrolled-user-name" title={user.email || user.Email || ""}>
                        {user.email || user.Email || "N/A"}
                      </span>
                    </td>
                    <td className="bg-transparent" style={{ verticalAlign: "middle" }}>
                      <div className="d-flex align-items-center gap-1">
                        <span className={`course-status-badge ${getStatusClass(user.courseStatusName)}`}>
                          {user.courseStatusName}
                        </span>
                        {isUserOverdue(user) && (
                          <span className="course-status-badge status-overdue" title={`Overdue (End Date: ${FormatDate(user.courseEndDate)})`}>
                            Overdue
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="bg-transparent text-center" style={{ verticalAlign: "middle" }}>
                      <div className="course-progress-wrapper">
                        <div className="course-progress-bar">
                          <div
                            className="course-progress-fill"
                            style={{ width: `${getProgressPercentage(user.completedModuleCount, user.totalModuleCount)}%` }}
                          ></div>
                        </div>
                        <span className="course-progress-text">
                          {user.completedModuleCount}/{user.totalModuleCount}
                        </span>
                      </div>
                    </td>
                    <td className="bg-transparent text-center" style={{ verticalAlign: "middle" }}>
                      {FormatDate(user.courseStartDate)}
                    </td>
                    <td className="bg-transparent text-center" style={{ verticalAlign: "middle" }}>
                      {FormatDate(user.courseEndDate)}
                    </td>
                    <td className="bg-transparent text-center" style={{ verticalAlign: "middle" }}>
                      <button
                        className="btn btn-sm course-module-toggle-btn"
                        disabled={!hasAtLeastOneQuizName(user)}
                        onClick={() => handleViewModules(user)}
                      >
                        View
                      </button>
                    </td>
                    <td className="bg-transparent text-center" style={{ verticalAlign: "middle" }}>
                      {(user.assignmentUrl || user.AssignmentUrl)?.trim() ? (
                        <a
                          href={(user.assignmentUrl || user.AssignmentUrl).trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm course-module-toggle-btn text-decoration-none d-inline-flex align-items-center justify-content-center"
                        >
                          View
                        </a>
                      ) : (
                        <button className="btn btn-sm course-module-toggle-btn" disabled>
                          View
                        </button>
                      )}
                    </td>
                    <td className="bg-transparent text-center" style={{ verticalAlign: "middle" }}>
                      <button className="btn p-0 m-0 border-0" type="button">
                        <Revoke
                          onClick={(e) => {
                            e.preventDefault();
                            handleRevokeClick(user.userId || user.id);
                          }}
                        />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td className="fw-bold course-enrolled-total-items" colSpan={2}>Total Items: {processedUsers.length}</td>
                <td colSpan={8}>
                  <div className="d-flex justify-content-end course-enrolled-pagination-wrap">
                    <div className="d-flex align-items-center">
                      <p className="me-2 mb-0 course-enrolled-res-per-page">Results per page</p>
                      <select
                        className="form-select m-2 course-enrolled-results-page"
                        value={paginationState.resultsPerPage}
                        onChange={(event) => {
                          setPaginationState((prevState) => ({
                            ...prevState,
                            resultsPerPage: Number(event.target.value),
                            currentPage: 1,
                          }));
                        }}
                        disabled={isLoading}
                      >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="30">30</option>
                      </select>
                      <ul className="pagination mb-0">
                        <li className={`page-item me-1 ${paginationState.currentPage === 1 ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => {
                              setPaginationState((prevState) => {
                                if (prevState.currentPage !== 1) {
                                  return {
                                    ...prevState,
                                    currentPage: prevState.currentPage - 1,
                                  };
                                }
                                return prevState;
                              });
                              window.scrollTo(0, 0);
                            }}
                            disabled={isLoading}
                          >
                            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M7 1.5L1.5 7L7 12.5" stroke={paginationState.currentPage === 1 ? "#A4A3A7" : "#28519E"} strokeWidth="1.5" />
                            </svg>
                          </button>
                        </li>
                        {paginationItems.map((item, index) =>
                          typeof item === "number" ? (
                            <li key={item} className={`page-item me-1 ${item === paginationState.currentPage ? "active" : ""}`}>
                              <button
                                className="page-link rounded course-enrolled-pagination-styling fw-bold"
                                onClick={() => {
                                  setPaginationState((prevState) => ({
                                    ...prevState,
                                    currentPage: item,
                                  }));
                                  window.scrollTo(0, 0);
                                }}
                                disabled={isLoading}
                              >
                                {item}
                              </button>
                            </li>
                          ) : (
                            <li key={`${item}-${index}`} className="page-item me-1 disabled">
                              <span className="page-link rounded course-enrolled-pagination-styling fw-bold">...</span>
                            </li>
                          )
                        )}
                        <li className={`page-item ${paginationState.currentPage === totalPaginationPages ? "disabled" : ""}`}>
                          <button
                            className="page-link"
                            onClick={() => {
                              setPaginationState((prevState) => {
                                if (prevState.currentPage < totalPaginationPages) {
                                  return {
                                    ...prevState,
                                    currentPage: prevState.currentPage + 1,
                                  };
                                }
                                return prevState;
                              });
                              window.scrollTo(0, 0);
                            }}
                            disabled={isLoading}
                          >
                            <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 12.5L6.5 7L1 1.5" stroke={paginationState.currentPage === totalPaginationPages ? "#A4A3A7" : "#28519E"} strokeWidth="1.5" />
                            </svg>
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
            </table>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton className="module-modal-header">
          <div className="module-modal-header-content">
            <Modal.Title className="module-modal-title">
              {selectedUser?.firstName} {selectedUser?.lastName} — Quiz Scores
            </Modal.Title>
            <div className="module-modal-header-meta">
              <span className="module-modal-progress-label module-modal-progress-label-on-header">
                Progress: {selectedUser?.completedModuleCount}/{selectedUser?.totalModuleCount} modules
              </span>
              <div className="d-flex align-items-center gap-1">
                <span className={`course-status-badge ${getStatusClass(selectedUser?.courseStatusName)} module-modal-status-badge`}>
                  {selectedUser?.courseStatusName}
                </span>
                {isUserOverdue(selectedUser) && (
                  <span className="course-status-badge status-overdue module-modal-status-badge" title={`Overdue (End Date: ${FormatDate(selectedUser?.courseEndDate)})`}>
                    Overdue
                  </span>
                )}
              </div>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="module-modal-body">
          <div className="module-table-section">
            <div className="module-table-section-title">Quiz Attempts</div>
            <table className="table mb-0 module-inner-table">
              <thead>
                <tr>
                  <th className="module-table-head">Quiz Name</th>
                  <th className="module-table-head text-center">Attempt No.</th>
                  <th className="module-table-head text-center">Score</th>
                  <th className="module-table-head text-center">Percentage</th>
                  <th className="module-table-head text-center">Pass/Fail</th>
                </tr>
              </thead>
              <tbody>
                {attemptRows && attemptRows.length > 0 ? (
                  attemptRows.map(({ module, attempt }, index) => (
                    <tr key={`${module.moduleId}-${attempt.attemptNo}-${index}`} className={((moduleGroupIndex[module?.moduleId ?? module?.moduleName ?? module?.quizName ?? JSON.stringify(module)] ?? 0) % 2 === 0) ? "module-even-row" : "module-odd-row"}>
                      <td className="module-name-cell">{attempt.quizName}</td>
                      <td className="text-center">{attempt.attemptNo}</td>
                      <td className="text-center">
                        {attempt.attemptNo === 0 || attempt.correctQuestions === null || attempt.correctQuestions === undefined || attempt.totalQuestions === null || attempt.totalQuestions === undefined
                          ? "N/A"
                          : `${attempt.correctQuestions}/${attempt.totalQuestions}`}
                      </td>
                      <td className="text-center">
                        {attempt.attemptNo === 0 || attempt.correctQuestions === null || attempt.correctQuestions === undefined || attempt.totalQuestions === null || attempt.totalQuestions === undefined
                          ? "N/A"
                          : `${((Number(attempt.correctQuestions) / Number(attempt.totalQuestions)) * 100).toFixed(2)}%`}
                      </td>
                      <td className="text-center">
                        <span className={`module-status-badge ${getAttemptStatusClass(attempt)}`}>
                          {getAttemptResultLabel(attempt)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-4">No quiz data available.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>


      {revokeModalState.isModalOpen && (
        <CustomModal
          modalProps={{
            icon: revokeModalState.icon,
            show: revokeModalState.isModalOpen,
            type: revokeModalState.modalType,
            title: revokeModalState.modalTitle,
            message: revokeModalState.modalMessage,
            cancelText: "No, Cancel",
            confirmText: "Yes, Revoke",
            onCancel: handleRevokeCancel,
            onConfirm: handleRevokeConfirm,
          }}
        />
      )}
    </>
  );
};

export default CourseEnrolledUsers;
